import II18 from '../translate/II18';
import {
  FORMELEMENTS_CHECKBOXES,
  FORMELEMENTS_DROPDOWN,
  FORMELEMENTS_RADIOBUTTONS,
  FORMELEMENTS_SIGNATURE,
  FORMELEMENTS_NUMBERINPUT,
  FORMELEMENTS_ACCEPT_BUTTON,
  FORMELEMENTS_DATEPICKER,
  FORMELEMENTS_TEXTAREA,
  FORMELEMENTS_TEXTINPUT,
  FORMELEMENTS_LABEL,
  FORMELEMENTS_PARAGRAPH,
  FORMELEMENTS_LINEBREAK,
  FORMELEMENTS_RECTANGLE,
  FORMELEMENTS_HYPERLINK,
  FORMELEMENTS_IMAGE,
  FORMELEMENTS_MOBILE_SPLIT_NUMBER
} from '../FormBuilder/FormElements';
import { displayLocaleOrUTCDate, getTodayUTCDate } from '../FormBuilder/utils';
import { arrToggle } from './immutable';
import { confirmDialog } from './dialogUtils';
import { f7 } from 'framework7-react';
import {
  ConditionalLogic,
  WidgetValidation
} from '@eqx/paperfly-conditional-lib';
import { DOC_TYPE_FORM, DOC_TYPE_PDF_FORM } from './utils';
import { ESIGNATURE_TYPE } from '../../redux/actions/rootAction';
import { messageIfInvalidPhone } from './formInputsUtils';
import dial_data from './country_dial_info';
import { EXTERNAL_API_VALIDATION_STAMP_KEY } from './externalApiValidationConstants';

export { EXTERNAL_API_VALIDATION_STAMP_KEY };

export const esMapToJsonObject = formDataMap => {
  const jsonData = {};
  formDataMap &&
    Array.from(formDataMap).forEach(([key, value]) => {
      if (key !== EXTERNAL_API_VALIDATION_STAMP_KEY) {
        jsonData[key] = value;
      }
    });
  return jsonData;
};
export const formsMapToJsonObject = forms => {
  const jsonData = {};
  forms &&
    Array.from(forms).forEach(
      ([key, value]) => (jsonData[key] = esMapToJsonObject(value))
    );
  return jsonData;
};

export const isRequiredValueOk = (item, value) => {
  return item.optional || isItemValueNotEmpty(item, value);
};

export const isItemValueNotEmpty = (item, value) => {
  switch (item.element) {
    case FORMELEMENTS_MOBILE_SPLIT_NUMBER:
      return value?.code && value?.number;
    case FORMELEMENTS_CHECKBOXES:
    case FORMELEMENTS_RADIOBUTTONS:
    case FORMELEMENTS_DROPDOWN:
      return (
        Array.isArray(value) && item.options.some(o => value.includes(o.id))
      );
    default:
      return (
        value !== undefined &&
        (Array.isArray(value) ? value.length > 0 : String(value).trim() !== '')
      );
  }
};

export const validateFormValueType = (item, value) => {
  if (value === undefined || value === '') return false;

  switch (item.element) {
    case FORMELEMENTS_MOBILE_SPLIT_NUMBER:
      return messageIfInvalidPhone(value?.code + value?.number);
    case FORMELEMENTS_NUMBERINPUT:
      switch (item.specialType) {
        default:
          return isNaN(value.replace(',', '.')) ? 'forms_wrong_digit' : false;
      }
    // no default
  }
};

export function getAge(dateString) {
  let today = new Date();
  let birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  let m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/**
 * Merge live field values into document `attributes.formData` so CONDITION
 * placeholders `{{DOCUMENT__docId__fieldId}}` resolve during validateWidget.
 * When `documentId` is set, only that document is updated; otherwise all docs.
 */
export const mergeLiveFormDataIntoDocuments = (
  requestedDocuments,
  liveFormData,
  documentId
) => {
  if (!requestedDocuments?.length || !liveFormData) {
    return requestedDocuments;
  }
  const formData =
    liveFormData instanceof Map
      ? esMapToJsonObject(liveFormData)
      : liveFormData;
  return requestedDocuments.map(doc => {
    if (documentId && doc.id !== documentId) {
      return doc;
    }
    return {
      ...doc,
      attributes: {
        ...doc.attributes,
        formData: {
          ...(doc.attributes?.formData || {}),
          ...formData
        }
      }
    };
  });
};

/**
 * @param item form widget
 * @param value current field value
 * @param requestedDocuments docs with `attributes.formData` (and structure)
 *   for CONDITION validation placeholders `{{DOCUMENT__docId__fieldId}}`
 */
export const validateFormItem = (item, value, requestedDocuments) => {
  let widgetValidationInLibrary = {
    isValid: true
  };
  if (value && !isBoxesWidget(item.element) && WidgetValidation) {
    widgetValidationInLibrary = {
      isValid: true,
      errorCodes: [],
      errorMessages: [],
      ...WidgetValidation.validateWidget(item, value, requestedDocuments)
    };
  }
  let errorMessage = '';
  if (
    item.element === FORMELEMENTS_DATEPICKER &&
    item.validate?.dateOfBirth18 &&
    value
  ) {
    if (getAge(value) < 18) {
      errorMessage = II18.get('address_dialog_dateOfBirth_err_18');
    }
  }
  return {
    errRequired: !isRequiredValueOk(item, value),
    errType:
      validateFormValueType(item, value) ||
      (!widgetValidationInLibrary.isValid &&
        widgetValidationInLibrary.errorCodes[0]),
    errMessage:
      (!widgetValidationInLibrary.isValid &&
        widgetValidationInLibrary.errorMessages[0]) ||
      errorMessage,
    element: item
  };
};

export const isFormItemValidationHasErrors = errors => {
  return errors.errRequired || errors.errType || errors.errMessage;
};

export const isFormItemValid = (item, value, requestedDocuments) => {
  return isFormItemValidationHasErrors(
    validateFormItem(item, value, requestedDocuments)
  );
};

export const getFormItemErrorMessage = (error, showRequiredErrors) => {
  return (
    error &&
    (error.errMessage
      ? error.errMessage
      : error.errType
      ? II18.get(error.errType)
      : error.errRequired && showRequiredErrors
      ? II18.get('forms_required')
      : '')
  );
};

export const isFormReadyToSignature = formErrors => {
  if (!formErrors.size) return 0;

  const errsArray = Array.from(formErrors);
  return errsArray.every(
    err => err[1].element.element === FORMELEMENTS_SIGNATURE
  )
    ? errsArray[0][1].element
    : false;
};

export const isFormReadyToAccept = formErrors => {
  if (!formErrors.size) return 0;

  const errsArray = Array.from(formErrors);
  return errsArray.every(
    err => err[1].element.element === FORMELEMENTS_ACCEPT_BUTTON
  )
    ? errsArray[0][1].element
    : false;
};

export const findAllFormErrors = (
  formStructure,
  formResult,
  requestedDocuments,
  documentId,
  preparedFormsData
) => {
  const docsWithLiveFormData = prepareDocumentsForFormValidation(
    requestedDocuments,
    preparedFormsData,
    formResult,
    documentId
  );
  const result = new Map();
  formStructure.forEach(page =>
    page.forEach(item => {
      if (!isFormItemNotEditable(item)) {
        const value = formResult.get(item.id);
        const itemValidation = validateFormItem(
          item,
          value,
          docsWithLiveFormData
        );
        isFormItemValidationHasErrors(itemValidation) &&
          result.set(item.id, itemValidation);
      }
    })
  );

  return result;
};

export const getFormData = (document, forms) => {
  return (document && forms.get(document.id)) || new Map();
};

export const isDocumentFormValid = (
  formStructure,
  formData,
  requestedDocuments,
  documentId
) => {
  const formErrors = findAllFormErrors(
    formStructure,
    formData,
    requestedDocuments,
    documentId
  );
  return formErrors.size === 0;
};

export const findFormItemById = (formStructure, itemId) => {
  return formStructure.reduce(
    (findItem, page) => findItem || page.find(i => i.id === itemId),
    undefined
  );
};

export const findFormItemByCustomId = (formStructure, customId) => {
  return formStructure.reduce(
    (findItem, page) => findItem || page.find(i => i.customId === customId),
    undefined
  );
};

/**
 * Check if default-value is regular value or not empty array and not array with one empty string element.
 *
 * @param defaultValues
 * @returns {boolean}
 */
export const widgetHasDefaultValue = defaultValues =>
  defaultValues &&
  (!Array.isArray(defaultValues) ||
    (defaultValues.length && defaultValues[0] !== ''));

export const parseInheritanceTokens = (
  text,
  destItem,
  allFormStructures,
  allFormsData,
  allowCustomIdCompare
) => {
  const tokenRegex = /\{\{DOCUMENT__([^_]+)__([^}]+)\}\}/g;

  let strictResult;
  let anyStringResult = '';
  try {
    const matches = text.match(tokenRegex);
    if (!matches?.length) {
      return applySourceItemInheritance(destItem, {}, text);
    }

    anyStringResult = text.replace(
      tokenRegex,
      (match, documentId, elementId) => {
        const fs = allFormStructures.get(documentId);

        const sourceItem = fs && findFormItemById(fs, elementId);
        const sourceValue = allFormsData.get(documentId)?.get(elementId);

        const value = applySourceItemInheritance(
          destItem,
          sourceItem,
          sourceValue,
          undefined,
          allowCustomIdCompare
        );
        if (
          value !== null &&
          value !== undefined &&
          typeof value === 'object'
        ) {
          strictResult = value;
          return '';
        } else return value === undefined ? '' : value;
      }
    );
  } catch (e) {
    return undefined;
  }
  return strictResult || anyStringResult;
};

export function applyValueInheritance(
  destItem,
  allFormsData,
  allFormStructures,
  allowCustomIdCompare
) {
  const valueInheritance = destItem.valueInheritance;

  if (
    ((valueInheritance?.documentId && valueInheritance?.fieldId) ||
      valueInheritance?.placeholdersStr) &&
    allFormsData &&
    allFormStructures
  ) {
    if (valueInheritance?.placeholdersStr) {
      return parseInheritanceTokens(
        valueInheritance?.placeholdersStr,
        destItem,
        allFormStructures,
        allFormsData,
        allowCustomIdCompare
      );
    }

    const sourceFormStructure = allFormStructures.get(
      valueInheritance.documentId
    );
    const sourceField =
      sourceFormStructure &&
      findFormItemById(sourceFormStructure, valueInheritance.fieldId);
    const inheritedValue = allFormsData
      .get(valueInheritance.documentId)
      ?.get(valueInheritance.fieldId);

    // No source (document/field hidden by conditions) - nothing to inherit.
    if (!sourceField && !inheritedValue) return undefined;

    return applySourceItemInheritance(
      destItem,
      sourceField,
      inheritedValue,
      valueInheritance.optionsMapping,
      allowCustomIdCompare
    );
  }
}

const filterOptionsAndGetIds = (options, filterFunction) =>
  options.filter(o => filterFunction(o)).map(o => o.id);

/**
 * Transform options array to Map of selected options:
 *  {optionId => option}
 *
 * @param allOptions - array of all options
 * @param usedOptionsId - array of selected options ids
 * @returns {optionId: option}
 */
const mapUsedOptions = (allOptions, usedOptionsId) => {
  return allOptions
    .filter(option => usedOptionsId.includes(option.id))
    .reduce((acc, option) => {
      acc[option.id] = option;
      return acc;
    }, {});
};

export const applySourceItemInheritance = (
  destItem,
  sourceField,
  sourceValue,
  destToSourceOptionMapping,
  allowCustomIdCompare
) => {
  if (sourceValue === undefined) return undefined;

  const destType = destItem.element;
  switch (sourceField?.element) {
    case FORMELEMENTS_DATEPICKER:
      return destType === FORMELEMENTS_DATEPICKER
        ? sourceValue
        : displayLocaleOrUTCDate(sourceValue);
    case FORMELEMENTS_CHECKBOXES:
    case FORMELEMENTS_RADIOBUTTONS:
    case FORMELEMENTS_DROPDOWN: {
      const selectedSourceOptionsMap = mapUsedOptions(
        sourceField?.options || [],
        sourceValue
      );

      if (isOptionsWidget(destType)) {
        if (destToSourceOptionMapping) {
          const result = filterOptionsAndGetIds(
            destItem.options,
            destOption =>
              selectedSourceOptionsMap[destToSourceOptionMapping[destOption.id]]
          );

          //by optionsMapping result must be strict, return immediately
          return result.length ? result : undefined;
        }

        //compare by customId
        if (allowCustomIdCompare) {
          const result = filterOptionsAndGetIds(destItem.options, destOption =>
            Object.entries(selectedSourceOptionsMap).some(
              ([, o]) =>
                destOption.customId && o.customId === destOption.customId
            )
          );

          if (result.length) return result;
        }

        //compare by label
        const result = filterOptionsAndGetIds(destItem.options, destOption =>
          Object.entries(selectedSourceOptionsMap).some(
            ([, o]) => destOption.label && o.label === destOption.label
          )
        );

        return result.length ? result : undefined;
      } else {
        return Object.entries(selectedSourceOptionsMap)
          .map(([, o]) => o.value || o.label)
          .join(destType === FORMELEMENTS_TEXTAREA ? '\n' : '; ');
      }
    }
    default:
      switch (destType) {
        case FORMELEMENTS_MOBILE_SPLIT_NUMBER:
          return splitPhoneNumber(sourceValue);
        case FORMELEMENTS_CHECKBOXES:
        case FORMELEMENTS_RADIOBUTTONS:
        case FORMELEMENTS_DROPDOWN: {
          const result = destItem.options.find(
            option => option.label === sourceValue || option.id === sourceValue
          );
          return result ? [result.id] : undefined;
        }
        default:
          return sourceValue || '';
      }
  }
};

export const setMobileNumberToFormField = (fieldType, number) => {
  switch (fieldType) {
    case FORMELEMENTS_MOBILE_SPLIT_NUMBER:
      return splitPhoneNumber(number);
    default:
      return number;
  }
};

export function splitPhoneNumber(number) {
  let result = {
    code: '+49',
    number: number || ''
  };
  dial_data.forEach(code => {
    if (number?.indexOf(code.dial_code) === 0) {
      result = {
        code: code.dial_code,
        number: number.slice(code.dial_code.length)
      };
    }
  });
  return result;
}

/**
 * 1. Set default values to formResult
 * 2. Set TodayDate to form result
 * 3. Take to formResult only data which appear in formStructure (clear from conditional-not-relevant data)
 *
 * @param formStructure
 * @param formData
 * @returns {Map<any, any>}
 */
export const applyDefaultValuesToFormResult = (formStructure, formData) => {
  const formResult = new Map(formData);
  formStructure.forEach(page =>
    page.forEach(item => {
      if (item.readOnly || !formData.has(item.id)) {
        if (widgetHasDefaultValue(item.defaultValues)) {
          let value =
            item.element === FORMELEMENTS_CHECKBOXES ||
            item.element === FORMELEMENTS_RADIOBUTTONS ||
            item.element === FORMELEMENTS_DROPDOWN
              ? item.defaultValues
              : item.defaultValues[0];

          formResult.set(item.id, value);
        } else if (
          item.forCurrentRecipient &&
          item.element === FORMELEMENTS_DATEPICKER &&
          item.dateTodayOption
        ) {
          formResult.set(item.id, getTodayUTCDate());
        }
      } else {
        formResult.set(item.id, formData.get(item.id));
      }
    })
  );

  return formResult;
};

export const clearFormsDataOfInvisibleElements = (
  formStructures,
  formsData
) => {
  const formsResult = new Map();
  for (let k of formStructures.keys()) {
    const formResult = clearFormDataOfInvisibleElements(
      formStructures.get(k),
      formsData.get(k)
    );
    formsResult.set(k, formResult);
  }

  return formsResult;
};

export const clearFormDataOfInvisibleElements = (formStructure, formData) => {
  const formResult = new Map();
  formStructure.forEach(page =>
    page.forEach(
      item =>
        formData.has(item.id) && formResult.set(item.id, formData.get(item.id))
    )
  );

  // Preserve client-only external-validation stamp across visibility filtering.
  if (formData?.has?.(EXTERNAL_API_VALIDATION_STAMP_KEY)) {
    formResult.set(
      EXTERNAL_API_VALIDATION_STAMP_KEY,
      formData.get(EXTERNAL_API_VALIDATION_STAMP_KEY)
    );
  }

  return formResult;
};

export const collectFormStructures = requestedDocumentsArray => {
  const formsStructures = new Map();

  requestedDocumentsArray
    .filter(doc => doc.type === DOC_TYPE_FORM || doc.type === DOC_TYPE_PDF_FORM)
    .forEach(doc => {
      formsStructures.set(doc.id, doc.formStructure);
    });

  return formsStructures;
};

/**
 * Attach cleared form values onto documents for CaseConditionalLogic
 * (`attributes.formData`), shared by document-list and single-condition checks.
 */
export const prepareDocumentsForCaseConditions = (
  requestedDocuments,
  preparedFormsData
) => {
  const formStructures = collectFormStructures(requestedDocuments);
  const clearedFormData = clearFormsDataOfInvisibleElements(
    formStructures,
    preparedFormsData
  );
  const jsonData = formsMapToJsonObject(clearedFormData);

  return requestedDocuments.map(d => ({
    ...d,
    attributes: {
      ...d.attributes,
      formData: jsonData[d.id]
    }
  }));
};

/**
 * Same docsForValidation path as evaluateDocumentsCurrentState / isDocumentReady:
 * all forms from preparedFormsData onto `attributes.formData`, with the document
 * under edit overridden by liveFormData (do not mutate preparedFormsData).
 */
export const prepareDocumentsForFormValidation = (
  requestedDocuments,
  preparedFormsData,
  liveFormData,
  documentId
) => {
  if (!requestedDocuments?.length) {
    return requestedDocuments;
  }
  if (!preparedFormsData) {
    return mergeLiveFormDataIntoDocuments(
      requestedDocuments,
      liveFormData,
      documentId
    );
  }
  const formsData = new Map(preparedFormsData);
  if (documentId && liveFormData) {
    formsData.set(
      documentId,
      liveFormData instanceof Map
        ? liveFormData
        : new Map(Object.entries(liveFormData))
    );
  }
  return prepareDocumentsForCaseConditions(requestedDocuments, formsData);
};

export const applyFormConditionsAndDefaultValuesToForms = (
  requestedDocumentsArray,
  formsData
) => {
  const preparedFormsStructures = new Map();
  const preparedFormsData = new Map();

  requestedDocumentsArray
    .filter(doc => doc.type === DOC_TYPE_FORM || doc.type === DOC_TYPE_PDF_FORM)
    .forEach(doc => {
      let formStructure = doc.formStructure;
      let formData = new Map(formsData.get(doc.id));
      if (doc.attributes.conditions && doc.attributes.conditions.length) {
        const conditionalLogic = new ConditionalLogic(
          formStructure,
          doc.attributes.conditions
        );
        const fData = {};
        Array.from(formData).forEach(([key, value]) => (fData[key] = value));
        formStructure = conditionalLogic.getConditionedFormStructure(fData);
        formData = clearFormDataOfInvisibleElements(formStructure, formData);
      }

      preparedFormsStructures.set(doc.id, formStructure);
      preparedFormsData.set(
        doc.id,
        applyDefaultValuesToFormResult(formStructure, formData)
      );
    });

  /*for (let [formId, formStructure] of preparedFormsStructures) {
    formStructure.forEach(page =>
      page.forEach(item => {
        const itemValue = applyValueInheritanceToFormItem(
          item,
          preparedFormsData.get(formId),
          preparedFormsData
        );
        if (itemValue !== undefined) {
          preparedFormsData.get(formId).set(item.id, itemValue);
        }
      })
    );
  }*/

  return {
    preparedFormsStructures,
    preparedFormsData
  };
};

export const applyValuesInheritanceToForms = (
  preparedFormsStructures,
  preparedFormsData
) => {
  for (let [formId, formStructure] of preparedFormsStructures) {
    formStructure.forEach(page =>
      page.forEach(item => {
        if (
          item.valueInheritance &&
          preparedFormsData.get(formId)?.get(item.id) === undefined
        ) {
          const itemValue = applyValueInheritance(
            item,
            preparedFormsData,
            preparedFormsStructures
          );
          if (itemValue !== undefined) {
            preparedFormsData.get(formId).set(item.id, itemValue);
          }
        }
      })
    );
  }

  return preparedFormsData;
};

export const recalculateFormWidgetsIndexes = formStructure => {
  return formStructure.map((page, pageIndex) =>
    page.map((item, elementIndex) => ({
      ...item,
      pageIndex: pageIndex,
      elementIndex: elementIndex
    }))
  );
};

export const setFormReadonlyMode = (formStructure, excludeQes) => {
  return formStructure?.map(page =>
    page.map(item => ({
      ...item,
      readOnly: excludeQes && item.isQesWidget ? item.readOnly : true
    }))
  );
};

export const formHasElementWithType = (
  preparedFormStructure,
  eType,
  required
) => {
  return preparedFormStructure.some(page =>
    page.some(
      item =>
        item.forCurrentRecipient &&
        item.element === eType &&
        (!required || !item.optional)
    )
  );
};

export const findElementsWithoutValue = (
  formStructure,
  formResult,
  skipIds
) => {
  const result = [];
  formStructure.forEach(page =>
    page.forEach(item => {
      if (
        !item.readOnly &&
        (!skipIds || !skipIds.includes(item.id)) &&
        !isItemValueNotEmpty(item, formResult.get(item.id))
      ) {
        result.push(item);
      }
    })
  );

  return result.length ? result : false;
};

export const formIsEmpty = formStructure => {
  return !formStructure.some(page => page.length);
};

/** Editable widgets on a form page (excludes layout / static / readOnly). */
export const countFormPageFields = (page = []) =>
  page.filter(item => !isFormItemNotEditable(item)).length;

export const isDocumentVisited = (visitedDocuments, documentId) =>
  Boolean(documentId && visitedDocuments?.has(documentId));

export const formExitDialog = (doExitLater, doExitNow, doReset) => {
  const buttons = [
    {
      text: II18.get('forms_fill_next'),
      onClick: doExitNow
    },
    {
      text: II18.get('forms_fill_later'),
      onClick: doExitLater
    }
  ];

  /*if (doReset) {
    buttons.push({
      text: II18.get('forms_cancel_changes'),
      onClick: doReset
    });
  }*/
  f7.dialog
    .create({
      title: II18.get('dialog_warning'),
      text: II18.get('dialog_fill_all_required'),
      buttons: buttons,
      verticalButtons: true
    })
    .open();
};

export const formStartQesDialog = (doSignLater, doSignNow) => {
  confirmDialog(
    II18.get('dialog_edit_will_off_after_qes_title'),
    II18.get('dialog_edit_will_off_after_qes'),
    doSignNow,
    doSignLater,
    II18.get('title_next'),
    II18.get('title_cancel'),
    !window.isDesktop
  );
};

export const resetFormDialog = onReset => {
  f7.dialog
    .create({
      title: II18.get('dialog_warning'),
      text: II18.get('dialog_changies_will_removed'),
      buttons: [
        {
          text: II18.get('dialog_continue'),
          onClick: onReset
        },
        {
          text: II18.get('title_cancel')
        }
      ],
      verticalButtons: true
    })
    .open();
};

export const isFormItemNotEditable = item => {
  return (
    item.readOnly ||
    item.static ||
    item.element === FORMELEMENTS_LABEL ||
    item.element === FORMELEMENTS_PARAGRAPH ||
    item.element === FORMELEMENTS_LINEBREAK ||
    item.element === FORMELEMENTS_RECTANGLE ||
    item.element === FORMELEMENTS_HYPERLINK ||
    item.element === FORMELEMENTS_IMAGE
  );
};

export const queryByFormItemId = (inputId, subCluss) => {
  return document.querySelector('[id="' + inputId + '"]' + subCluss);
};

export const itemUsingKeyboard = elementType => {
  return (
    elementType === FORMELEMENTS_NUMBERINPUT ||
    elementType === FORMELEMENTS_TEXTAREA ||
    elementType === FORMELEMENTS_TEXTINPUT
  );
};

export function getPdfImageProps(pdfMetaData) {
  return pdfMetaData.dpiModifier
    ? {
        imageWidth: pdfMetaData.originalWidth,
        imageHeight: pdfMetaData.originalHeight
      }
    : {
        imageWidth: pdfMetaData.imageWidth,
        imageHeight: pdfMetaData.imageHeight
      };
}

/*
export const findFormElementByType = (formStructure, type) => {
  let result = false;
  formStructure.some(page =>
    page.some(item => {
      if (item.element === type) {
        result = item;
        return true;
      } else return false;
    })
  );
};
*/

export const updateFormBoxesValue = (
  itemId,
  optId,
  elementType,
  formResult
) => {
  let curValue = formResult.get(itemId);

  if (!curValue || elementType === 'RadioButtons') {
    return [optId];
  } else {
    return arrToggle(curValue, optId);
  }
};

export const getConfirmRestSignaturesMessage = signatures => {
  return (
    '<div style="text-align: left"><p>' +
    II18.get('title_done_request_with_sign_confirm') +
    '</p>' +
    '<span style="display: block;">' +
    II18.get('title_done_request_rest_signatures') +
    '</span>' +
    '<ul style="max-height: 100px; overflow: auto;">' +
    signatures.map(item => '<li>' + item.label + '</li>').join('') +
    '</ul></div>'
  );
};

export const checkIfFormDoesNotNeedConfirm = (
  preparedFormStructure,
  formResult
) => {
  const notReadyItems = findElementsWithoutValue(
    preparedFormStructure,
    formResult
  );
  const notReadySignatures =
    notReadyItems &&
    notReadyItems.filter(item => item.element === FORMELEMENTS_SIGNATURE);

  if (notReadySignatures && notReadySignatures.length > 0) {
    return notReadySignatures;
  } else {
    return (
      formHasElementWithType(
        preparedFormStructure,
        FORMELEMENTS_SIGNATURE,
        true
      ) ||
      formHasElementWithType(
        preparedFormStructure,
        FORMELEMENTS_ACCEPT_BUTTON,
        true
      ) ||
      !notReadyItems
    );
  }
};

export const isBoxesWidget = elementType =>
  elementType === FORMELEMENTS_CHECKBOXES ||
  elementType === FORMELEMENTS_RADIOBUTTONS;

export const isOptionsWidget = elementType =>
  elementType === FORMELEMENTS_DROPDOWN ||
  elementType === FORMELEMENTS_CHECKBOXES ||
  elementType === FORMELEMENTS_RADIOBUTTONS;

export const getExternalApiFormValue = (item, formValue) => {
  if (!isOptionsWidget(item.element) || !Array.isArray(formValue)) {
    return formValue;
  }

  return formValue
    .map(
      optionId => item.options?.find(option => option.id === optionId)?.customId
    )
    .filter(Boolean)
    .join(';');
};

export const getSignNowTitle = (item, qesProvider) => {
  if (!item.isQesWidget) return II18.get('forms_sign');

  switch (qesProvider) {
    case ESIGNATURE_TYPE.SIGNME:
      return II18.get('forms_sign');
    case ESIGNATURE_TYPE.YES:
      return II18.get('forms_sign_with_yes');
    case ESIGNATURE_TYPE.ATRUST:
      return II18.get('forms_sign_with_atrust');
    default:
      return II18.get('forms_sign');
  }
};
